import { useCallback, useEffect, useMemo } from 'react';
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { MenuOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { Dropdown, MenuProps, Spin } from 'antd';
import cx from 'classnames';
import Logo from 'assets/Logo';
import { useUser } from 'stores/user/userHooks';
import { useAppDispatch } from 'stores/hooks';
import { resetUser } from 'stores/user/userSlice';
import HubspotChat from 'components/hubspotchat/HubspotChat';

const ADMIN_ITEMS = [
  {
    url: '/admin',
    content: 'Administration',
    isSelected: (pathname: string) => pathname.includes('/admin'),
  },
];
const linkItems = [
  {
    url: '/',
    content: 'Agenda',
  },
  {
    url: '/patients',
    content: 'Mes patients',
    isSelected: (pathname: string) => pathname.includes('/patients'),
  },
  {
    url: '/account',
    content: 'Mon compte',
  },
];

export default function MainLayout() {
  const dispatch = useAppDispatch();
  const [user] = useUser();
  const navigate = useNavigate();
  const currentPathname = useLocation().pathname;
  const containerClassName = cx(
    'flex',
    'flex-col',
    'min-h-screen',
    'min-w-screen',
    'bg-background',
  );
  const onClickMenuItem = useCallback(
    ({ key }: { key: string }) => navigate(key),
    [navigate],
  );
  const items = useMemo(() => (user?.is_staff ? ADMIN_ITEMS : linkItems), [user?.is_staff]);
  const menuProps: MenuProps = useMemo(
    () => ({
      items: items.map(({
        url,
        content,
      }) => ({
        key: url,
        label: content,
        onClick: onClickMenuItem,
      })),
      selectedKeys: [currentPathname],
    }),
    [currentPathname, items, onClickMenuItem],
  );

  useEffect(() => () => {
    dispatch(resetUser());
  }, [dispatch]);

  if (!user) {
    return (
      <div className={cx(containerClassName, 'justify-center items-center')}>
        <Spin />
      </div>
    );
  }

  if (!user.activated) {
    return (
      <Navigate
        to='/new-account'
        replace
      />
    );
  }

  if (user.is_staff && !currentPathname.includes('/admin')) {
    return (
      <Navigate
        to='/admin'
        replace
      />
    );
  }

  if (!user.is_staff && currentPathname.includes('/admin')) {
    return (
      <Navigate
        to='/'
        replace
      />
    );
  }

  return (
    <div className={containerClassName}>
      <header className='fixed z-10 w-full bg-background-z-10 drop-shadow-xl'>
        <div className='flex justify-between w-full py-3 mx-auto max-w-screen-2xl px-14'>
          <Link
            className='mr-4'
            to='/'
          >
            <Logo width={143} />
          </Link>
          <div className='flex items-center justify-end grow'>
            {items.map(({
              url,
              content,
              isSelected = (pathname: string, itemUrl: string) => pathname === itemUrl,
            }) => (
              <Link
                to={url}
                key={url}
                className={cx(
                  'lg:text-xl text-lg mr-5 lg:mr-10 hidden md:block',
                  { 'font-bold': isSelected(currentPathname, url) },
                )}
              >
                {content}
              </Link>
            ))}
            <a
              href='https://hapni.doxy.me/sign-in'
              target='_blank'
              rel='noreferrer noopener'
              className={cx(
                'flex lg:h-[40px] items-center text-white rounded-full bg-gradient relative',
                'lg:pr-4 pr-0 lg:pl-12 pl-0',
              )}
            >
              <span
                className={cx(
                  'w-[42px] h-[42px] inline-flex flex-col justify-center items-center',
                  '-left-px -top-px lg:border-2 border-white rounded-full p-0.5 lg:absolute',
                )}
              >
                <VideoCameraOutlined />
              </span>
              <span className='hidden lg:inline-block'>
                Cabinet virtuel
              </span>
            </a>
            <div className='ml-4 md:hidden'>
              <Dropdown
                trigger={['click']}
                menu={menuProps}
              >
                <MenuOutlined className='text-2xl' />
              </Dropdown>
            </div>
          </div>
        </div>
      </header>
      <main className='flex flex-col w-full pt-[134px] pb-8 mx-auto max-w-screen-2xl px-14 grow'>
        <Outlet />
      </main>
      <HubspotChat />
    </div>
  );
}
